import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Spin, Tooltip } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist";
import apiClient from "utilities/apiClient";
import {
  displayRotationForTarget,
  findHitTargetAtPoint,
  findHitTargetByText,
  findTextHighlightSpanIndexes,
  getValidNormalizedPolygon,
  measureTextLayerBoxes,
  polygonToSvgPoints,
  refineHitTargetsWithTextBoxes,
} from "./documentHighlightUtil";
import {
  clientPointToPagePoint,
  isImageBytes,
  isPdfBytes,
  selectDocumentViewerSource,
  sniffImageContentType,
} from "./documentViewerUtil";

// Bundle the pdf.js worker with webpack 5 (no CDN dependency).
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.1;
const THUMB_SCALE = 0.25;
const PDF_LOAD_TIMEOUT_MS = 12000;

/**
 * DocumentViewer — PDF.js-based source document viewer.
 * - loads the original PDF via authenticated API (avoids Azure blob CORS),
 *   with an image fallback for non-PDF sources
 * - selectable text layer (copy invoice numbers / IBAN)
 * - zoom persisted across page changes, rotation via the pdf.js native API
 * - fullscreen, thumbnails, and ←/→ / PageUp / PageDown keyboard navigation
 * - stays in sync with the treatment list via currentIndex + onChange
 */
const DocumentViewer = ({
  documentId,
  pdfUrl,
  imageList,
  fileName,
  currentIndex,
  onChange,
  highlight,
  hitTargets = [],
  onTargetHover,
  onTargetSelect,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const pageSurfaceRef = useRef(null);
  const textItemsRef = useRef([]);
  const textDivsRef = useRef([]);
  const hoverTargetIdRef = useRef(null);

  const [mode, setMode] = useState("loading"); // loading | pdf | image | error
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [thumbs, setThumbs] = useState([]);
  const [textLayerVersion, setTextLayerVersion] = useState(0);
  const [hoverTarget, setHoverTarget] = useState(null);
  const [resolvedHitTargets, setResolvedHitTargets] = useState(hitTargets);
  const [failedImageUrl, setFailedImageUrl] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [streamedImageUrl, setStreamedImageUrl] = useState("");
  const streamedImageUrlRef = useRef("");

  useEffect(() => {
    streamedImageUrlRef.current = streamedImageUrl;
  }, [streamedImageUrl]);

  useEffect(
    () => () => {
      if (streamedImageUrlRef.current) {
        URL.revokeObjectURL(streamedImageUrlRef.current);
      }
    },
    []
  );

  const imagePageCount = streamedImageUrl
    ? 1
    : imageList?.length || 0;
  const page = Math.min(
    Math.max(currentIndex || 1, 1),
    mode === "pdf" ? numPages || 1 : imagePageCount || 1
  );

  const source = useMemo(
    () =>
      selectDocumentViewerSource({
        currentIndex,
        imageList,
        failedImageUrl,
        documentId,
        pdfUrl,
        fileName,
      }),
    [
      currentIndex,
      imageList,
      failedImageUrl,
      documentId,
      pdfUrl,
      fileName,
    ]
  );

  useEffect(() => {
    setFailedImageUrl("");
  }, [documentId]);

  // ---- Select and load the document surface -------------------------------
  useEffect(() => {
    setLoadError(null);
    setHoverTarget(null);
    hoverTargetIdRef.current = null;
    textItemsRef.current = [];
    textDivsRef.current = [];
    setTextLayerVersion(0);
    setThumbs([]);
    setStreamedImageUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });

    if (source.kind === "image") {
      setMode("image");
      setPdfDoc(null);
      setNumPages(0);
      return undefined;
    }

    if (source.kind === "unavailable") {
      setMode("error");
      setPdfDoc(null);
      setNumPages(0);
      setLoadError("unavailable");
      return undefined;
    }

    let cancelled = false;
    let committed = false;
    let loadingTask = null;
    let loadedDoc = null;
    let timeoutId = 0;
    const abortController = new AbortController();

    setMode("loading");
    setPdfDoc(null);
    setNumPages(0);

    const fail = (reason) => {
      if (cancelled || committed) return;
      committed = true;
      setPdfDoc(null);
      setNumPages(0);
      setLoadError(reason);
      setMode("error");
    };

    // The deadline covers both the authenticated download and PDF parsing.
    timeoutId = window.setTimeout(() => {
      abortController.abort();
      loadingTask?.destroy?.().catch?.(() => {});
      fail("timeout");
    }, PDF_LOAD_TIMEOUT_MS);

    (async () => {
      try {
        let pdfSource = null;
        if (source.kind === "pdf-api") {
          const response = await apiClient.getArrayBuffer(
            source.apiPath,
            undefined,
            { signal: abortController.signal }
          );
          if (cancelled) return;
          const contentType = String(
            response?.headers?.["content-type"] || ""
          ).toLowerCase();
          // PDF.js may transfer the underlying buffer to its worker.
          const bytes = new Uint8Array(response.data).slice();
          const isPdf = contentType.includes("pdf") || isPdfBytes(bytes);
          const imageType =
            (contentType.startsWith("image/") && contentType) ||
            sniffImageContentType(bytes);
          if (isPdf && bytes.length) {
            pdfSource = { data: bytes };
          } else if ((imageType || isImageBytes(bytes)) && bytes.length) {
            const blob = new Blob([bytes], {
              type: imageType || "image/jpeg",
            });
            const objectUrl = URL.createObjectURL(blob);
            window.clearTimeout(timeoutId);
            if (cancelled || committed) {
              URL.revokeObjectURL(objectUrl);
              return;
            }
            committed = true;
            setStreamedImageUrl(objectUrl);
            setPdfDoc(null);
            setNumPages(0);
            setMode("image");
            return;
          } else {
            throw new Error("not-a-pdf");
          }
        } else if (source.kind === "pdf-url") {
          pdfSource = {
            url: source.url,
            disableRange: true,
            disableStream: true,
          };
        } else {
          throw new Error("no-pdf-source");
        }

        if (cancelled) return;
        loadingTask = pdfjsLib.getDocument(pdfSource);
        loadedDoc = await loadingTask.promise;
        window.clearTimeout(timeoutId);
        if (cancelled || committed) {
          loadedDoc.destroy().catch(() => {});
          return;
        }
        committed = true;
        setPdfDoc(loadedDoc);
        setNumPages(loadedDoc.numPages);
        setMode("pdf");
      } catch (err) {
        window.clearTimeout(timeoutId);
        if (!cancelled) {
          fail(err?.status === 401 ? "unauthorized" : "load");
        }
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      abortController.abort();
      if (loadedDoc) {
        loadedDoc.destroy().catch(() => {});
      } else if (loadingTask) {
        loadingTask.destroy().catch(() => {});
      }
    };
  }, [source.kind, source.key, source.apiPath, source.url]);

  // ---- Render the current page (canvas + text layer) ----------------------
  useEffect(() => {
    if (mode !== "pdf" || !pdfDoc) return;
    let cancelled = false;
    let renderTask = null;
    let textTask = null;
    (async () => {
      try {
        const pdfPage = await pdfDoc.getPage(page);
        if (cancelled) return;
        const totalRotation = (pdfPage.rotate + rotation) % 360;
        const viewport = pdfPage.getViewport({ scale, rotation: totalRotation });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext("2d");
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const transform =
          outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;
        renderTask = pdfPage.render({ canvasContext: context, viewport, transform });
        await renderTask.promise;
        if (cancelled) return;

        // The canvas is usable even when text extraction fails.
        const textLayer = textLayerRef.current;
        if (textLayer) {
          textLayer.innerHTML = "";
          textLayer.style.width = `${Math.floor(viewport.width)}px`;
          textLayer.style.height = `${Math.floor(viewport.height)}px`;
          textLayer.style.setProperty("--scale-factor", scale);
          try {
            const textContent = await pdfPage.getTextContent();
            if (cancelled) return;
            const textDivs = [];
            // pdfjs-dist@3 expects textContentSource.
            textTask = pdfjsLib.renderTextLayer({
              textContentSource: textContent,
              container: textLayer,
              viewport,
              textDivs,
            });
            await textTask.promise;
            if (cancelled) return;
            textItemsRef.current = textContent.items || [];
            textDivsRef.current = textDivs;
            setTextLayerVersion((value) => value + 1);
          } catch (textError) {
            if (!cancelled) {
              textLayer.innerHTML = "";
              textItemsRef.current = [];
              textDivsRef.current = [];
            }
          }
        }
      } catch (err) {
        if (
          !cancelled &&
          !(err instanceof pdfjsLib.RenderingCancelledException)
        ) {
          setLoadError("render");
          setMode("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
      textTask?.cancel?.();
    };
  }, [mode, pdfDoc, page, scale, rotation]);

  const overlayRotation = mode === "pdf" ? rotation : 0;

  const getPageSurfaceEl = useCallback(
    () => pageSurfaceRef.current || canvasRef.current || null,
    []
  );

  // Snap highlights onto the rendered PDF text layer (PDF.js space ≠ OCR image space).
  useEffect(() => {
    if (!hitTargets?.length) {
      setResolvedHitTargets([]);
      return undefined;
    }
    if (mode !== "pdf" || !textLayerVersion) {
      setResolvedHitTargets(hitTargets);
      return undefined;
    }

    let cancelled = false;
    let outerRaf = 0;
    let innerRaf = 0;
    outerRaf = window.requestAnimationFrame(() => {
      innerRaf = window.requestAnimationFrame(() => {
        if (cancelled) return;
        const surface = getPageSurfaceEl();
        if (!surface) {
          setResolvedHitTargets(hitTargets);
          return;
        }
        const boxes = measureTextLayerBoxes(textDivsRef.current, surface);
        setResolvedHitTargets(
          refineHitTargetsWithTextBoxes(hitTargets, boxes, {
            textItems: textItemsRef.current,
            textDivs: textDivsRef.current,
            referenceEl: surface,
          })
        );
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerRaf);
      window.cancelAnimationFrame(innerRaf);
    };
  }, [hitTargets, mode, textLayerVersion, scale, rotation, getPageSurfaceEl]);

  const resolveOverlayTarget = useCallback(
    (candidate) => {
      if (!candidate) return null;
      if (candidate.itemKey || candidate.field) {
        const matched = resolvedHitTargets.find(
          (target) =>
            target.pageNo === (candidate.pageNo || page) &&
            target.field === (candidate.field || "Item Name") &&
            (candidate.itemKey
              ? target.itemKey === candidate.itemKey
              : !target.itemKey) &&
            (candidate.treatmentKey
              ? target.treatmentKey === candidate.treatmentKey
              : true)
        );
        if (matched?.sourceBbox) return matched;
      }
      return candidate;
    },
    [page, resolvedHitTargets]
  );

  const activeHighlight = resolveOverlayTarget(highlight);
  const activeHover = resolveOverlayTarget(hoverTarget);

  // On PDF, OCR image boxes often don't share the PDF.js coordinate space.
  // Only draw polygons that were snapped to the text layer (or image-mode OCR).
  const trustsDisplayBox = (target) =>
    !!getValidNormalizedPolygon(target?.sourceBbox) &&
    (mode !== "pdf" ||
      target?.snappedFromText ||
      target?.geometrySpace === "display");

  const hasBbox =
    activeHighlight?.pageNo === page && trustsDisplayBox(activeHighlight);
  const highlightPoints = hasBbox
    ? polygonToSvgPoints(
        activeHighlight.sourceBbox,
        // PDF.js rotates the viewport itself. Images rotate their entire page
        // surface, including this overlay, so rotating the points as well
        // would apply the image rotation twice.
        // Text-snapped boxes are already in display space.
        displayRotationForTarget(activeHighlight, overlayRotation)
      )
    : null;
  const hoverPoints =
    activeHover?.pageNo === page && trustsDisplayBox(activeHover)
      ? polygonToSvgPoints(
          activeHover.sourceBbox,
          displayRotationForTarget(activeHover, overlayRotation)
        )
      : null;

  const eventToNormalizedPoint = useCallback(
    (event) => {
      const surface = getPageSurfaceEl();
      if (!surface) return null;
      return clientPointToPagePoint({
        clientX: event.clientX,
        clientY: event.clientY,
        rect: surface.getBoundingClientRect(),
        // PDF rotation is baked into the viewport/text geometry. Image
        // rotation is CSS-only, so pointer coordinates must be unrotated.
        rotation: mode === "image" ? rotation : 0,
      });
    },
    [getPageSurfaceEl, mode, rotation]
  );

  const resolveTargetFromEvent = useCallback(
    (event) => {
      // Ignore OCR image-space polygons on PDF — they drift vs the text layer.
      const hitTestTargets =
        mode === "pdf"
          ? resolvedHitTargets.map((target) =>
              target.snappedFromText || target.geometrySpace === "display"
                ? target
                : { ...target, sourceBbox: null }
            )
          : resolvedHitTargets;

      const point = eventToNormalizedPoint(event);
      if (point) {
        const geometric = findHitTargetAtPoint(
          hitTestTargets,
          point.x,
          point.y,
          {
            pageNo: page,
            rotation: overlayRotation,
          }
        );
        if (geometric) return geometric;
      }

      const textEl = event.target?.closest?.(".textLayer span");
      const rawText = textEl?.textContent || "";
      if (rawText.trim()) {
        return findHitTargetByText(hitTestTargets, rawText, {
          pageNo: page,
        });
      }
      return null;
    },
    [eventToNormalizedPoint, mode, overlayRotation, page, resolvedHitTargets]
  );

  const emitHover = useCallback(
    (target) => {
      const nextId = target?.id || null;
      if (hoverTargetIdRef.current === nextId) return;
      hoverTargetIdRef.current = nextId;
      setHoverTarget(target || null);
      onTargetHover?.(target || null);
    },
    [onTargetHover]
  );

  const handlePagePointerMove = useCallback(
    (event) => {
      if (!resolvedHitTargets?.length) return;
      emitHover(resolveTargetFromEvent(event));
    },
    [emitHover, resolvedHitTargets, resolveTargetFromEvent]
  );

  const handlePagePointerLeave = useCallback(() => {
    emitHover(null);
  }, [emitHover]);

  const handlePageClick = useCallback(
    (event) => {
      if (!resolvedHitTargets?.length) return;
      // Ignore toolbar / thumb interactions — this handler is only on the page surface.
      const target = resolveTargetFromEvent(event);
      if (!target) return;
      event.preventDefault();
      emitHover(target);
      onTargetSelect?.(target);
    },
    [emitHover, onTargetSelect, resolveTargetFromEvent, resolvedHitTargets]
  );

  useEffect(() => {
    // Clear stale hover when page/targets change.
    hoverTargetIdRef.current = null;
    setHoverTarget(null);
  }, [page, resolvedHitTargets]);

  useEffect(() => {
    const textDivs = textDivsRef.current || [];
    textDivs.forEach((div) => div.classList.remove("docviewer-text-hit"));
    if (
      mode !== "pdf" ||
      hasBbox ||
      highlight?.pageNo !== page ||
      !highlight?.query
    ) {
      return;
    }
    const indexes = findTextHighlightSpanIndexes(
      textItemsRef.current,
      highlight.query,
      highlight.occurrence
    );
    indexes.forEach((index) => {
      textDivs[index]?.classList.add("docviewer-text-hit");
    });
  }, [highlight, page, mode, hasBbox, textLayerVersion, scale, rotation]);

  useEffect(() => {
    if (!highlight || highlight.pageNo !== page) return;
    const timer = window.setTimeout(() => {
      const stage = stageRef.current;
      const target =
        containerRef.current?.querySelector(".docviewer-bbox-hit") ||
        containerRef.current?.querySelector(".docviewer-text-hit");
      if (!stage || !target) return;
      const stageRect = stage.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const outside =
        targetRect.top < stageRect.top ||
        targetRect.bottom > stageRect.bottom ||
        targetRect.left < stageRect.left ||
        targetRect.right > stageRect.right;
      if (!outside) return;
      stage.scrollBy({
        top:
          targetRect.top -
          stageRect.top -
          stage.clientHeight / 2 +
          targetRect.height / 2,
        left:
          targetRect.left -
          stageRect.left -
          stage.clientWidth / 2 +
          targetRect.width / 2,
        behavior: "smooth",
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [highlight, highlightPoints, page, textLayerVersion, scale, rotation]);

  const highlightOverlay =
    highlightPoints || hoverPoints ? (
      <svg
        className="docviewer-highlight-layer"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {hoverPoints && hoverPoints !== highlightPoints ? (
          <polygon
            className="docviewer-bbox-hover"
            points={hoverPoints}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        {highlightPoints ? (
          <polygon
            className="docviewer-bbox-hit"
            points={highlightPoints}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
    ) : null;

  // ---- Render thumbnails once the doc is loaded ---------------------------
  useEffect(() => {
    if (mode !== "pdf" || !pdfDoc) return;
    let cancelled = false;
    (async () => {
      const results = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled) return;
        try {
          const pdfPage = await pdfDoc.getPage(i);
          const viewport = pdfPage.getViewport({ scale: THUMB_SCALE });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          await pdfPage.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
          results.push({ page: i, url: canvas.toDataURL() });
          if (!cancelled) setThumbs([...results]);
        } catch (err) {
          // skip a failed thumbnail
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, pdfDoc]);

  // ---- Fullscreen ----------------------------------------------------------
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (containerRef.current) {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  };

  // ---- Navigation ----------------------------------------------------------
  const goToPage = useCallback(
    (next) => {
      const max = mode === "pdf" ? numPages : imagePageCount || 1;
      const clamped = Math.min(Math.max(next, 1), max);
      if (clamped !== page && onChange) onChange(clamped);
    },
    [mode, numPages, imagePageCount, page, onChange]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        e.target?.isContentEditable
      ) {
        return; // never hijack typing in form fields
      }
      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goToPage(page - 1);
      } else if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        goToPage(page + 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToPage, page]);

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)));
  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const totalPages = mode === "pdf" ? numPages : imagePageCount;
  const viewerReady = mode === "pdf" || mode === "image";

  // ---- Toolbar -------------------------------------------------------------
  const toolbar = (
    <div className="docviewer-toolbar">
      <Tooltip title={t("zoomIn")}>
        <button type="button" className="docviewer-btn" aria-label={t("zoomIn")} onClick={zoomIn} disabled={!viewerReady}>
          <ZoomInOutlined />
        </button>
      </Tooltip>
      <Tooltip title={t("zoomOut")}>
        <button type="button" className="docviewer-btn" aria-label={t("zoomOut")} onClick={zoomOut} disabled={!viewerReady}>
          <ZoomOutOutlined />
        </button>
      </Tooltip>
      <span className="docviewer-zoompct">{Math.round(scale * 100)}%</span>
      <Tooltip title={t("rotateLeft")}>
        <button type="button" className="docviewer-btn" aria-label={t("rotateLeft")} onClick={rotateLeft} disabled={!viewerReady}>
          <RotateLeftOutlined />
        </button>
      </Tooltip>
      <Tooltip title={t("rotateRight")}>
        <button type="button" className="docviewer-btn" aria-label={t("rotateRight")} onClick={rotateRight} disabled={!viewerReady}>
          <RotateRightOutlined />
        </button>
      </Tooltip>
      <Tooltip title={isFullscreen ? t("exitFullscreen") : t("fullscreen")}>
        <button type="button" className="docviewer-btn" aria-label={isFullscreen ? t("exitFullscreen") : t("fullscreen")} onClick={toggleFullscreen}>
          {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        </button>
      </Tooltip>
      <span className="docviewer-pagenav">
        <button type="button" className="docviewer-btn" aria-label={t("previousPage")} onClick={() => goToPage(page - 1)} disabled={page <= 1}>
          <LeftOutlined />
        </button>
        <span className="docviewer-pageinfo">
          {page} {t("pageOf")} {totalPages}
        </span>
        <button type="button" className="docviewer-btn" aria-label={t("nextPage")} onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
          <RightOutlined />
        </button>
      </span>
    </div>
  );

  return (
    <div className="docviewer" ref={containerRef}>
      {mode === "loading" && (
        <div className="docviewer-loading">
          <Spin size="large" />
        </div>
      )}

      {mode === "error" && (
        <div className="docviewer-loading docviewer-error" data-error={loadError}>
          <p className="mb-2">{t("previewNotAvailable")}</p>
          {pdfUrl ? (
            <a className="docviewer-download" href={pdfUrl}>
              <DownloadOutlined /> {t("download")}
            </a>
          ) : null}
        </div>
      )}

      {mode === "pdf" && (
        <div className="docviewer-stage" ref={stageRef}>
          <div className="docviewer-canvas-wrap">
            <div
              ref={pageSurfaceRef}
              className={`docviewer-page-surface${
                hoverTarget ? " docviewer-page-surface--hot" : ""
              }`}
              onPointerMove={handlePagePointerMove}
              onPointerLeave={handlePagePointerLeave}
              onClick={handlePageClick}
            >
              <canvas ref={canvasRef} className="docviewer-canvas" />
              {highlightOverlay}
              <div ref={textLayerRef} className="textLayer" />
            </div>
          </div>
        </div>
      )}

      {mode === "image" && (
        <div className="docviewer-stage" ref={stageRef}>
          <div className="docviewer-canvas-wrap docviewer-image-wrap">
            <div
              ref={pageSurfaceRef}
              className={`docviewer-page-surface${
                hoverTarget ? " docviewer-page-surface--hot" : ""
              }`}
              style={{
                transform: `scale(${scale}) rotate(${rotation}deg)`,
              }}
              onPointerMove={handlePagePointerMove}
              onPointerLeave={handlePagePointerLeave}
              onClick={handlePageClick}
            >
              <img
                src={streamedImageUrl || source.imageUrl}
                alt={t("invoiceDocument")}
                className="docviewer-image"
                onError={() => {
                  if (streamedImageUrl) {
                    setMode("error");
                    setLoadError("load");
                    return;
                  }
                  setFailedImageUrl(source.imageUrl);
                }}
              />
              {highlightOverlay}
            </div>
          </div>
        </div>
      )}

      {toolbar}

      {/* Thumbnail sidebar (PDF-rendered) or image thumbnail strip */}
      {totalPages > 1 && (
        <div className="docviewer-thumbs">
          {mode === "pdf" &&
            thumbs.map((thumb) => (
              <button
                key={thumb.page}
                type="button"
                className={`docviewer-thumb ${thumb.page === page ? "docviewer-thumb--active" : ""}`}
                onClick={() => goToPage(thumb.page)}
                aria-label={`${t("page")} ${thumb.page}`}
              >
                <img src={thumb.url} alt={`${t("page")} ${thumb.page}`} />
                <span className="docviewer-thumb-num">{thumb.page}</span>
              </button>
            ))}
          {mode === "image" &&
            (imageList || []).map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`docviewer-thumb ${index + 1 === page ? "docviewer-thumb--active" : ""}`}
                onClick={() => goToPage(item.id)}
                aria-label={`${t("page")} ${index + 1}`}
              >
                <img src={item.image_url} alt={`${t("page")} ${index + 1}`} />
                <span className="docviewer-thumb-num">{index + 1}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;
